import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Mic, Square, Volume2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const creditsError = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (error?.response?.status === 402 && d?.code === 'credits_insufficient') {
    toast.error(`Crédits insuffisants : ${d.needed} nécessaires, solde ${d.balance ?? 0}. Rechargez dans Mon abonnement.`);
  } else toast.error(fallback);
};

// Question dictée : micro → Whisper (3 crédits) → texte envoyé à l'expert.
export const VoiceAskButton = ({ onText, disabled, testId = 'chat-mic-btn' }) => {
  const [state, setState] = useState('idle'); // idle | recording | sending
  const rec = useRef(null);
  const chunks = useRef([]);
  const supported = typeof window !== 'undefined' && !!(navigator.mediaDevices && window.MediaRecorder);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: r.mimeType || 'audio/webm' });
        if (blob.size < 1200) { setState('idle'); return; }
        setState('sending');
        try {
          const fd = new FormData();
          fd.append('audio', blob, r.mimeType?.includes('mp4') ? 'question.mp4' : 'question.webm');
          const res = await axios.post(`${API}/ai/voice/transcribe`, fd, { withCredentials: true, timeout: 60000 });
          const text = (res.data?.text || '').trim();
          if (!text) toast.info('Je n’ai rien entendu, réessayez plus près du micro.');
          else onText(text);
        } catch (error) { creditsError(error, 'Transcription impossible'); }
        setState('idle');
      };
      r.start();
      rec.current = r;
      setState('recording');
    } catch { toast.error('Micro non autorisé sur cet appareil'); }
  };
  const stop = () => { try { rec.current?.stop(); } catch { setState('idle'); } };
  useEffect(() => () => { try { rec.current?.state === 'recording' && rec.current.stop(); } catch { /* noop */ } }, []);
  if (!supported) return null;

  return <button type="button" onClick={state === 'recording' ? stop : start} disabled={disabled || state === 'sending'}
    className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors disabled:opacity-50 ${state === 'recording' ? 'bg-red-500 text-[var(--sur-couleur)] animate-pulse' : 'bg-[var(--remplissage)] text-[var(--encre)] hover:bg-[var(--remplissage)]'}`}
    aria-label={state === 'recording' ? 'Terminer et envoyer (3 crédits)' : 'Poser ma question à la voix (3 crédits)'} title={state === 'recording' ? 'Terminer et envoyer' : 'Poser ma question à la voix · 3 crédits'}
    data-testid={testId} data-state={state}>
    {state === 'sending' ? <Loader2 size={18} className="animate-spin" /> : state === 'recording' ? <Square size={16} /> : <Mic size={18} />}
  </button>;
};

// Réponse lue à la voix par l'expert (5 crédits), lecture/arrêt.
export const SpeakButton = ({ text, agentKey, testId }) => {
  const [state, setState] = useState('idle'); // idle | loading | playing
  const audio = useRef(null);
  const stop = () => { audio.current?.pause(); audio.current = null; setState('idle'); };
  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const play = async () => {
    setState('loading');
    try {
      const fd = new FormData(); fd.append('text', text); fd.append('agent_key', agentKey || 'sabril');
      const res = await axios.post(`${API}/ai/voice/speak`, fd, { withCredentials: true, responseType: 'blob', timeout: 60000 });
      const a = new Audio(URL.createObjectURL(res.data));
      a.onended = stop; audio.current = a; await a.play(); setState('playing');
    } catch (error) {
      if (error?.response?.data instanceof Blob) { try { error.response.data = JSON.parse(await error.response.data.text()); } catch { /* noop */ } }
      creditsError(error, 'Lecture impossible'); setState('idle');
    }
  };
  return <button type="button" onClick={state === 'playing' ? stop : play} disabled={state === 'loading'}
    className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg transition-colors ${state === 'playing' ? 'bg-[var(--brand)] text-[var(--sur-couleur)]' : 'text-[var(--texte-2)] hover:bg-[var(--remplissage)]'}`}
    title={state === 'playing' ? 'Arrêter la lecture' : 'Écouter la réponse · 5 crédits'} aria-label={state === 'playing' ? 'Arrêter la lecture' : 'Écouter la réponse (5 crédits)'} data-testid={testId} data-state={state}>
    {state === 'loading' ? <Loader2 size={14} className="animate-spin" /> : state === 'playing' ? <Square size={12} /> : <Volume2 size={14} />}
    {state === 'playing' ? 'Stop' : 'Écouter'}
  </button>;
};
